import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ClipboardList, Loader2, CheckCircle2, ChevronRight, User as UserIcon,
  Pencil, Trash2, UserPlus, Save, X,
} from "lucide-react";
import { toast } from "sonner";

export default function WorkOrderQueue() {
  const nav = useNavigate();
  const [data, setData] = useState({ mine: [], available: [], done_today: 0 });
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  // Edit modal
  const [editingWo, setEditingWo] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  // Assign modal
  const [assigningWo, setAssigningWo] = useState(null);
  const [assignTechId, setAssignTechId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/work-orders/queue");
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => {
      const techs = (r.data || []).filter((u) => (u.role || "").toLowerCase() === "technician");
      setTechnicians(techs);
    }).catch(() => {});
  }, []);

  const canAdmin = me && ["admin", "manager"].includes((me.role || "").toLowerCase());

  const startEdit = (wo) => {
    setEditingWo(wo);
    setEditDraft({
      scheduled_at: wo.scheduled_at ? String(wo.scheduled_at).slice(0, 16) : "",
      priority: wo.priority || "normal",
      notes: wo.notes || "",
      customer_name: wo.customer_name || "",
      customer_phone: wo.customer_phone || "",
    });
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editDraft };
      if (payload.scheduled_at === "") payload.scheduled_at = null;
      await api.patch(`/work-orders/${editingWo.id}`, payload);
      toast.success("Work Order atualizada");
      setEditingWo(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };

  const remove = async (wo) => {
    if (!window.confirm(`Excluir Work Order #${wo.id.slice(0, 8)}?\nEla ficará arquivada (não sai do histórico).`)) return;
    try {
      await api.delete(`/work-orders/${wo.id}`);
      toast.success("Work Order arquivada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao excluir"); }
  };

  const startAssign = (wo) => {
    setAssigningWo(wo);
    setAssignTechId(wo.execution?.assigned_tech_id || "");
  };

  const doAssign = async () => {
    if (!assignTechId) return toast.error("Selecione um técnico");
    try {
      await api.post(`/work-orders/${assigningWo.id}/assign`, { tech_id: assignTechId });
      const tech = technicians.find((t) => t.id === assignTechId);
      toast.success(`Atribuída a ${tech?.name || "técnico"}`);
      setAssigningWo(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao atribuir"); }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="work-orders-queue-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-400" /> Work Orders
          </h1>
          <p className="text-sm text-slate-400 mt-1">Fila de execução do técnico + admin edit/assign/delete.</p>
        </div>
        <Badge variant="outline" className="border-emerald-500/30 text-emerald-300 bg-emerald-500/10" data-testid="kpi-done-today">
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {data.done_today} done today
        </Badge>
      </header>

      <section>
        <h2 className="text-sm uppercase tracking-wider text-slate-500 mb-2">My queue ({data.mine.length})</h2>
        {data.mine.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg p-4" data-testid="empty-mine">
            Nada em progresso. Pegue uma da lista abaixo.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.mine.map((wo) => (
              <WorkOrderCard
                key={wo.id} wo={wo} mine canAdmin={canAdmin}
                onEdit={startEdit} onDelete={remove} onAssign={startAssign}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm uppercase tracking-wider text-slate-500 mb-2">Available ({data.available.length})</h2>
        {data.available.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg p-4" data-testid="empty-available">
            Sem work orders sem atribuição.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.available.map((wo) => (
              <WorkOrderCard
                key={wo.id} wo={wo} canAdmin={canAdmin}
                onEdit={startEdit} onDelete={remove} onAssign={startAssign}
              />
            ))}
          </div>
        )}
      </section>

      {canAdmin && (
        <section>
          <h2 className="text-sm uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
            <UserIcon className="w-3.5 h-3.5 text-amber-400" />
            Em Andamento (atribuídas a outros técnicos) ({(data.in_progress || []).length})
          </h2>
          {(!data.in_progress || data.in_progress.length === 0) ? (
            <div className="text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg p-4" data-testid="empty-in-progress">
              Nenhuma work order em andamento com outros técnicos.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="in-progress-section">
              {data.in_progress.map((wo) => (
                <WorkOrderCard
                  key={wo.id} wo={wo} canAdmin={canAdmin} inProgressForOthers
                  onEdit={startEdit} onDelete={remove} onAssign={startAssign}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Edit modal */}
      <Dialog open={!!editingWo} onOpenChange={(o) => !o && setEditingWo(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-blue-400" />Editar Work Order</DialogTitle>
          </DialogHeader>
          {editingWo && (
            <div className="space-y-3 mt-2" data-testid="edit-wo-modal">
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Data agendada</Label>
                <Input data-testid="edit-scheduled" type="datetime-local" value={editDraft.scheduled_at || ""} onChange={(e) => setEditDraft({ ...editDraft, scheduled_at: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Prioridade</Label>
                <select data-testid="edit-priority" value={editDraft.priority} onChange={(e) => setEditDraft({ ...editDraft, priority: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md h-9 px-3 text-sm text-slate-200">
                  <option value="low">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Cliente</Label>
                  <Input value={editDraft.customer_name || ""} onChange={(e) => setEditDraft({ ...editDraft, customer_name: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
                <div><Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Telefone</Label>
                  <Input value={editDraft.customer_phone || ""} onChange={(e) => setEditDraft({ ...editDraft, customer_phone: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" /></div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Notas</Label>
                <textarea data-testid="edit-notes" rows={3} value={editDraft.notes || ""} onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md p-2 text-sm text-slate-200" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingWo(null)} className="border-slate-700"><X className="w-4 h-4 mr-1" />Cancelar</Button>
                <Button data-testid="edit-save-btn" onClick={saveEdit} className="bg-blue-600 hover:bg-blue-500"><Save className="w-4 h-4 mr-1" />Salvar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign modal */}
      <Dialog open={!!assigningWo} onOpenChange={(o) => !o && setAssigningWo(null)}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-emerald-400" />Atribuir Técnico</DialogTitle>
          </DialogHeader>
          {assigningWo && (
            <div className="space-y-3 mt-2" data-testid="assign-wo-modal">
              <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
                Work Order: <span className="font-mono text-slate-100">#{assigningWo.id.slice(0, 8)}</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Técnico</Label>
                <select data-testid="assign-tech-select" value={assignTechId} onChange={(e) => setAssignTechId(e.target.value)} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md h-9 px-3 text-sm text-slate-200">
                  <option value="">-- selecione --</option>
                  {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {technicians.length === 0 && <div className="text-[10px] text-amber-400 mt-1">Nenhum técnico cadastrado. Adicione em Admin → User Management (role=technician).</div>}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAssigningWo(null)} className="border-slate-700">Cancelar</Button>
                <Button data-testid="assign-confirm-btn" onClick={doAssign} className="bg-emerald-600 hover:bg-emerald-500"><UserPlus className="w-4 h-4 mr-1" />Atribuir</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WorkOrderCard({ wo, mine, canAdmin, onEdit, onDelete, onAssign, inProgressForOthers }) {
  const exe = wo.execution || {};
  const statusColor = exe.status === "in_progress"
    ? "bg-amber-500/15 border-amber-500/30 text-amber-300"
    : exe.status === "completed"
    ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
    : "bg-slate-500/15 border-slate-500/30 text-slate-300";

  const stopProp = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(wo); };

  return (
    <div
      data-testid={`wo-card-${wo.id}`}
      className={`relative border rounded-xl p-4 hover:border-blue-500/40 transition group ${
        inProgressForOthers
          ? "bg-amber-500/5 border-amber-500/30 hover:bg-amber-500/10"
          : "bg-slate-900/60 border-slate-800 hover:bg-slate-900"
      }`}
    >
      <Link to={`/work-orders/${wo.id}`} className="block">
        <div className="flex items-start justify-between mb-2">
          <div className="text-xs text-slate-500 font-mono">#{wo.id.slice(0, 8)}</div>
          <Badge variant="outline" className={statusColor}>{exe.status || "pending"}</Badge>
        </div>
        <div className="text-slate-100 font-semibold text-sm leading-tight mb-1 line-clamp-2">
          {wo.source_filename || wo.customer_name || "Calibration Report"}
        </div>
        {wo.scheduled_at && (
          <div className="text-[10px] text-cyan-300 mt-1">📅 {String(wo.scheduled_at).slice(0, 16).replace("T", " ")}</div>
        )}
        <div className="text-xs text-slate-400 flex items-center gap-1 mt-2">
          {exe.assigned_tech_name ? (
            <><UserIcon className="w-3 h-3" /> {exe.assigned_tech_name}</>
          ) : (
            <span className="text-slate-500">Unassigned</span>
          )}
        </div>
      </Link>

      {/* Admin actions row */}
      {canAdmin && (
        <div className="mt-3 pt-3 border-t border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-1">
            <button data-testid={`wo-assign-${wo.id}`} onClick={stopProp(onAssign)} title="Atribuir técnico" className="p-1.5 rounded hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-300"><UserPlus className="w-4 h-4" /></button>
            <button data-testid={`wo-edit-${wo.id}`} onClick={stopProp(onEdit)} title="Editar" className="p-1.5 rounded hover:bg-blue-500/10 text-slate-400 hover:text-blue-300"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`wo-delete-${wo.id}`} onClick={stopProp(onDelete)} title="Excluir" className="p-1.5 rounded hover:bg-rose-500/10 text-slate-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
          </div>
          <Link to={`/work-orders/${wo.id}`} className="text-blue-400 text-xs hover:text-blue-300 flex items-center">Abrir <ChevronRight className="w-3 h-3 ml-0.5" /></Link>
        </div>
      )}
      {!canAdmin && (
        <div className="mt-3 flex items-center justify-end text-blue-400 text-xs group-hover:text-blue-300">
          <Link to={`/work-orders/${wo.id}`}>Abrir <ChevronRight className="w-3 h-3 ml-0.5 inline" /></Link>
        </div>
      )}
    </div>
  );
}
